import { useEffect, useSyncExternalStore } from 'react';
import { type Theme as ShellThemeMode } from '../components/admin/theme-context';
import { useTheme as useShellTheme } from '../components/admin/use-theme';

export type ResolvedThemeMode = 'light' | 'dark';

const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

export const resolveThemeMode = (
  mode: ShellThemeMode,
  prefersDarkMode: boolean
): ResolvedThemeMode =>
  mode === 'system' ? (prefersDarkMode ? 'dark' : 'light') : mode;

const subscribeToDarkMode = (onChange: () => void) => {
  const mediaQuery = window.matchMedia(DARK_MODE_QUERY);
  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
};

const getPrefersDarkMode = () => window.matchMedia(DARK_MODE_QUERY).matches;

export const usePrefersDarkMode = () =>
  useSyncExternalStore(subscribeToDarkMode, getPrefersDarkMode, () => false);

/** Resolves the kit theme setting for integrations that need `light | dark`. */
export const useResolvedThemeMode = (): ResolvedThemeMode => {
  const { theme } = useShellTheme();
  const prefersDarkMode = usePrefersDarkMode();
  const resolvedMode = resolveThemeMode(theme, prefersDarkMode);

  // The kit ThemeProvider updates the class when the setting changes. Keep the
  // class aligned with OS changes too when `system` is selected.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedMode === 'dark');
    root.classList.toggle('light', resolvedMode === 'light');
  }, [resolvedMode]);

  return resolvedMode;
};
